import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { useToast } from "@/hooks/use-toast"
import { useCreatePromotion, useUpdatePromotion, type Promotion, type PromotionCurrency } from "@/api/promotions"
import { ImageIcon } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const CURRENCY_OPTIONS: { value: PromotionCurrency; label: string }[] = [
  { value: "ALL", label: "ALL" },
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
]

const promotionSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  price: z.coerce.number().min(0, "Price must be 0 or more"),
  discountedPrice: z.union([z.coerce.number().min(0), z.literal("")]).optional(),
  currency: z.enum(["ALL", "EUR", "USD"]),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
  isActive: z.boolean(),
})

type PromotionFormValues = z.infer<typeof promotionSchema>

interface PromotionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  promotion?: Promotion | null
}

export function PromotionDialog({ open, onOpenChange, promotion }: PromotionDialogProps) {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [photoBase64, setPhotoBase64] = useState<string>("")
  const [photoPreview, setPhotoPreview] = useState<string>("")

  const createPromotion = useCreatePromotion()
  const updatePromotion = useUpdatePromotion()

  const isEditing = !!promotion

  const form = useForm<PromotionFormValues>({
    resolver: zodResolver(promotionSchema),
    defaultValues: {
      title: "",
      description: "",
      price: 0,
      discountedPrice: "",
      currency: "ALL",
      startDate: new Date().toISOString().split("T")[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      isActive: true,
    },
  })

  useEffect(() => {
    if (promotion) {
      form.reset({
        title: promotion.title,
        description: promotion.description,
        price: promotion.price,
        discountedPrice: promotion.discountedPrice ?? "",
        currency: (promotion.currency as PromotionCurrency) || "ALL",
        startDate: promotion.startDate.split("T")[0],
        endDate: promotion.endDate.split("T")[0],
        isActive: promotion.isActive,
      })
      setPhotoPreview(promotion.photo)
      setPhotoBase64("")
    } else {
      form.reset({
        title: "",
        description: "",
        price: 0,
        discountedPrice: "",
        currency: "ALL",
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        isActive: true,
      })
      setPhotoPreview("")
      setPhotoBase64("")
    }
  }, [promotion, open])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (ev) => {
      const result = ev.target?.result as string
      setPhotoBase64(result)
      setPhotoPreview(result)
    }
    reader.readAsDataURL(file)
  }

  const onSubmit = async (values: PromotionFormValues) => {
    const photoToSend = photoBase64 || (isEditing ? undefined : "")

    if (!isEditing && !photoBase64) {
      toast({ title: "Error", description: "Please select a photo", variant: "destructive" })
      return
    }

    const payload = {
      title: values.title,
      description: values.description,
      price: values.price,
      discountedPrice: values.discountedPrice !== "" ? Number(values.discountedPrice) : undefined,
      currency: values.currency,
      startDate: values.startDate,
      endDate: values.endDate,
      isActive: values.isActive,
      ...(photoToSend ? { photo: photoToSend } : {}),
    }

    try {
      if (isEditing) {
        await updatePromotion.mutateAsync({ id: promotion._id, data: payload })
        toast({ title: "Promotion updated" })
      } else {
        await createPromotion.mutateAsync({ ...payload, photo: photoBase64 })
        toast({ title: "Promotion created" })
      }
      onOpenChange(false)
    } catch {
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" })
    }
  }

  const isPending = createPromotion.isPending || updatePromotion.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[580px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Promotion" : "Add Promotion"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Photo upload */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Photo (16:9 recommended)</p>
              <div
                className="relative w-full cursor-pointer overflow-hidden rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 hover:bg-muted/50 transition-colors"
                style={{ aspectRatio: "16/9" }}
                onClick={() => fileInputRef.current?.click()}
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-1 text-muted-foreground">
                    <ImageIcon className="h-8 w-8" />
                    <span className="text-sm">Click to upload image</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
              {photoPreview && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Change photo
                </Button>
              )}
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Summer membership deal" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Describe the promotion..." rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" step="0.01" placeholder="99.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="discountedPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Discounted Price (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="79.00"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-md border p-3">
                  <FormLabel className="cursor-pointer">Active</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : isEditing ? "Save changes" : "Create promotion"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
